import React, { useState, useRef, useEffect } from 'react';
import type { ChatMessage, MediaPart } from '../types';
import { generateChatResponse } from '../services/geminiService';
import ChatMessageComponent from '../components/ChatMessage';
import { SendIcon, LogoIcon, DocumentIcon, ChevronDownIcon, UploadIcon, FileIcon, ImageIcon, VideoIcon, MusicIcon, PdfIcon, XCircleIcon } from '../components/icons';

const examplePrompts = [
    "Show me my GA4 report for the last 7 days",
    "Analyze my GTM container",
    "Suggest a new ad for our 'Q4 Sales' campaign",
    "Create a GTM tag to track contact form submissions",
];

const MAX_FILE_SIZE_MB = 10;
const MAX_FILES = 5;
const SUPPORTED_MIME_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif", "video/mp4", "video/mpeg", "video/webm", "audio/mpeg", "audio/wav", "audio/ogg"];

type ContextFile = {
    name: string;
    part: MediaPart;
};

const fileToBase64 = (file: File): Promise<MediaPart> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            const result = reader.result as string;
            const data = result.split(',')[1];
            resolve({ inlineData: { data, mimeType: file.type } });
        };
        reader.onerror = error => reject(error);
    });
};

const FileTypeIcon: React.FC<{ mimeType: string }> = ({ mimeType }) => {
    if (mimeType.startsWith('image/')) return <ImageIcon className="w-5 h-5 text-text-secondary" />;
    if (mimeType.startsWith('video/')) return <VideoIcon className="w-5 h-5 text-text-secondary" />;
    if (mimeType.startsWith('audio/')) return <MusicIcon className="w-5 h-5 text-text-secondary" />;
    if (mimeType === 'application/pdf') return <PdfIcon className="w-5 h-5 text-text-secondary" />;
    return <FileIcon className="w-5 h-5 text-text-secondary" />;
};


const ChatView: React.FC = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [context, setContext] = useState('');
  const [contextFiles, setContextFiles] = useState<ContextFile[]>([]);
  const [isContextOpen, setIsContextOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const files = event.target.files;
    if (!files) return;

    if (contextFiles.length + files.length > MAX_FILES) {
        setError(`You can upload a maximum of ${MAX_FILES} files.`);
        return;
    }

    // FIX: Replaced for...of loop with a standard for loop to ensure `file` is correctly typed as `File`.
    // The original loop caused TypeScript to incorrectly infer `file` as `unknown`.
    for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
            setError(`File "${file.name}" exceeds the ${MAX_FILE_SIZE_MB}MB size limit.`);
            continue;
        }
        if (!SUPPORTED_MIME_TYPES.includes(file.type)) {
            setError(`File type for "${file.name}" is not supported. Supported types are images, video, and audio.`);
            continue;
        }

        try {
            const mediaPart = await fileToBase64(file);
            setContextFiles(prev => [...prev, { name: file.name, part: mediaPart }]);
        } catch (err) {
            setError(`Failed to process file "${file.name}".`);
        }
    }
  };

  const removeFile = (fileName: string) => {
    setContextFiles(prev => prev.filter(f => f.name !== fileName));
  };
  
  const handleSend = async (prompt?: string) => {
    const userMessageContent = prompt || input;
    if (!userMessageContent.trim() || isLoading) return;
    
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: userMessageContent,
    };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    setError(null);

    // Construct the multi-part content
    const parts: (MediaPart | { text: string })[] = [];
    
    // Add file context first
    contextFiles.forEach(file => parts.push(file.part));

    // Add text context
    if (context.trim()) {
      parts.push({ text: `Use the following text as context:\n---\n${context.trim()}\n---` });
    }

    // Add the main prompt last
    parts.push({ text: userMessageContent });
    
    try {
      const modelResponseContent = await generateChatResponse({ parts });
      const modelMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'model',
        content: modelResponseContent,
      };
      setMessages(prev => [...prev, modelMessage]);
    } catch (error) {
        const errorMessageContent = error instanceof Error ? error.message : "I'm sorry, an error occurred while processing your request.";
        const errorMessage: ChatMessage = {
            id: (Date.now() + 1).toString(),
            role: 'model',
            content: errorMessageContent,
        };
        setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-4 md:p-6">
        <div className="max-w-4xl mx-auto">
          {messages.length === 0 && (
            <div className="text-center py-10">
                <div className="inline-block p-4 bg-brand-primary rounded-full mb-4">
                    <LogoIcon className="w-12 h-12 text-white"/>
                </div>
                <h2 className="text-2xl font-bold">How can I help you today?</h2>
                <p className="text-text-secondary mt-2">Start by asking about your marketing data or for creative ideas.</p>
                 <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-8">
                    {examplePrompts.map(prompt => (
                        <button key={prompt} onClick={() => handleSend(prompt)} className="text-left p-4 bg-base-200 hover:bg-base-300 rounded-lg transition-colors text-sm">
                            {prompt}
                        </button>
                    ))}
                </div>
            </div>
          )}
          {messages.map(msg => <ChatMessageComponent key={msg.id} message={msg} />)}
          {isLoading && (
            <div className="flex items-start gap-4 my-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-brand-primary">
                    <LogoIcon className="w-6 h-6 text-white animate-pulse" />
                </div>
                <div className="max-w-2xl p-4 rounded-lg bg-base-200">
                    <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-text-secondary rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-text-secondary rounded-full animate-bounce [animation-delay:0.2s]"></div>
                        <div className="w-2 h-2 bg-text-secondary rounded-full animate-bounce [animation-delay:0.4s]"></div>
                    </div>
                </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>
      </div>
      
      <footer className="p-4 bg-base-100 flex-shrink-0 border-t border-base-300">
        <div className="max-w-4xl mx-auto">
          <div className="mb-2">
            <button
              onClick={() => setIsContextOpen(!isContextOpen)}
              className="flex items-center gap-2 text-sm text-text-secondary hover:text-text-primary transition-colors"
            >
              <DocumentIcon className="w-5 h-5" />
              <span>Provide Additional Context (Text or Files)</span>
              <ChevronDownIcon className={`w-4 h-4 transition-transform ${isContextOpen ? 'rotate-180' : ''}`} />
            </button>
            {isContextOpen && (
              <div className="mt-2 p-3 bg-base-200 rounded-lg border border-base-300 space-y-3">
                <textarea
                  value={context}
                  onChange={(e) => setContext(e.target.value)}
                  placeholder="Paste a marketing brief, product description, or other text..."
                  className="w-full bg-base-300 rounded-md p-2 border border-base-300 focus:border-brand-primary focus:outline-none resize-y text-sm"
                  rows={4}
                />
                
                <div className="space-y-2">
                    <button onClick={() => fileInputRef.current?.click()} className="flex items-center gap-2 text-sm font-medium bg-base-300 hover:bg-brand-primary/20 px-3 py-2 rounded-md transition-colors">
                        <UploadIcon className="w-5 h-5"/>
                        Add Files (Image, Video, Audio)
                    </button>
                     <input type="file" ref={fileInputRef} onChange={handleFileChange} multiple accept={SUPPORTED_MIME_TYPES.join(',')} className="hidden" />
                    {contextFiles.length > 0 && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2">
                            {contextFiles.map(file => (
                                <div key={file.name} className="flex items-center justify-between bg-base-300 p-2 rounded-md text-sm">
                                    <div className="flex items-center gap-2 overflow-hidden">
                                        <FileTypeIcon mimeType={file.part.inlineData.mimeType} />
                                        <span className="truncate text-text-secondary">{file.name}</span>
                                    </div>
                                    <button onClick={() => removeFile(file.name)} className="text-text-secondary hover:text-red-400">
                                        <XCircleIcon className="w-5 h-5"/>
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                {error && <p className="text-red-400 text-xs mt-1">{error}</p>}
              </div>
            )}
          </div>
          <div className="flex items-center bg-base-200 rounded-lg p-2 border border-base-300 focus-within:border-brand-primary">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder="Ask about your GA4 data, analyze context, or request ad suggestions..."
              className="flex-1 bg-transparent focus:outline-none resize-none p-2"
              rows={1}
            />
            <button
              onClick={() => handleSend()}
              disabled={isLoading || !input.trim()}
              className="p-2 rounded-md bg-brand-primary text-white disabled:bg-base-300 disabled:text-base-100 transition-colors"
              aria-label="Send message"
            >
              <SendIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ChatView;